import React, { useMemo, useRef } from 'react';
import { ErrorLabel, FieldLabel, fieldValidationErrorMessage } from '@myosh/odin-components';
import type { DynamicFieldComponentProps, DynamicFormButtonSetting, JsonDataItem } from '@myosh/odin-components';
import clsx from 'clsx';
import { isEmpty, orderBy } from 'lodash';
import type { FieldError } from 'react-hook-form';
import { Observable } from 'rxjs';
import QuestionnaireQuestion from './questionnaire-question.component';
import NumericQuestionnaireScore from './numeric-questionnaire-score.component';
import QuestionnaireScore from './questionnaire-score.component';
import type {
  QuestionnaireConfigProps,
  QuestionnaireFieldValue,
  QuestionnaireValueItem,
  QuestionnaireItemValidationErrors,
  QuestionnaireItemValidationMap,
  QuestionnaireValue,
} from '../../../@types/questionnaire';
import type { RecordResult } from '../../../@types/records';
import type { AttachmentsPayloadProps } from '../../../@types/attachments';

interface QuestionnaireFieldError extends FieldError {
  value?: Array<QuestionnaireItemValidationErrors>;
}

export interface QuestionnaireProps extends DynamicFieldComponentProps<QuestionnaireFieldValue> {
  configuration?: QuestionnaireConfigProps;
  recordId?: string;
  saveRecord?: () => Observable<RecordResult>;
  errorObject?: QuestionnaireFieldError;
  submitCount: number;
  setFormButtonsState?: React.Dispatch<React.SetStateAction<DynamicFormButtonSetting[]>>;
  getRecordData: () => JsonDataItem;
  updateRecordDataToIncludeAttachments: (params: AttachmentsPayloadProps) => void;
}

const Questionnaire = ({
  label,
  value,
  configuration,
  readOnly,
  required,
  onChange,
  id,
  recordId,
  registerPreSubmitHandler,
  registerPostSubmitHandler,
  saveRecord,
  errorObject,
  submitCount,
  setFormButtonsState,
  getRecordData,
  updateRecordDataToIncludeAttachments,
}: QuestionnaireProps) => {
  const answers = value?.value ?? [];
  const answersRef = useRef<QuestionnaireValue>(answers);
  answersRef.current = answers;

  const showScores = configuration?.displayScores && answers.length > 0;

  const handleChange = (item?: QuestionnaireValueItem) => {
    if (item) {
      const questionIndex = configuration?.questions.findIndex((question) => question.id === item.questionId);
      const question =
        questionIndex !== undefined && questionIndex !== -1 ? configuration?.questions[questionIndex] : undefined;
      const questionPosition = question?.position;
      const linkedRecordIds = item.linkedRecordIds
        ? item.linkedRecordIds
        : item.linkedRecords?.map((record) => String(record.id));
      const existingAnswerIndex = answers.findIndex((answer) => answer.questionId === item.questionId);

      const updatedItem: QuestionnaireValueItem = {
        ...item,
        linkedRecordIds,
        questionPosition,
      };

      const formattedAnswer: QuestionnaireValueItem[] = answers.map((answer) => {
        return {
          ...answer,
          linkedRecordIds: answer.linkedRecords?.map((record) => String(record.id)),
        };
      });

      if (existingAnswerIndex !== -1) {
        formattedAnswer[existingAnswerIndex] = updatedItem;
      } else {
        formattedAnswer.push(updatedItem);
      }

      if (onChange) {
        onChange({
          value: orderBy(formattedAnswer, ['questionPosition'], ['asc']),
        });
      } else {
        throw new Error('The <Questionnaire/> should not be used directly. Please use <HfQuestionnaire/> instead.');
      }
    }
  };

  const validationErrorMap: QuestionnaireItemValidationMap = useMemo(() => {
    const errorMap: QuestionnaireItemValidationMap = {};

    if (errorObject?.value) {
      for (let i = 0; i < errorObject.value.length; i++) {
        const errorItem = errorObject.value[i];
        const attachmentsMessage = errorItem?.attachments?.message;
        if (attachmentsMessage) {
          const { questionId, message } = attachmentsMessage;
          errorMap[questionId] = { ...(errorMap[questionId] ?? {}), attachments: message };
        }

        const observationsMessage = errorItem?.observations?.message;
        if (observationsMessage) {
          const { questionId, message } = observationsMessage;
          errorMap[questionId] = { ...(errorMap[questionId] ?? {}), observations: message };
        }

        const linkedRecordsMessage = errorItem?.linkedRecords?.message;
        if (linkedRecordsMessage) {
          const { questionId, message } = linkedRecordsMessage;
          errorMap[questionId] = { ...(errorMap[questionId] ?? {}), linkedRecords: message };
        }
      }
    }

    return errorMap;
  }, [errorObject?.value]);

  const containerClasses = clsx('flex flex-col gap-2 pt-2', {
    'font-bold': !label,
  });

  return (
    <div className="flex flex-col">
      {label && <div className="flex">{configuration?.showFieldCaption && <FieldLabel label={label} />}</div>}
      <div className={containerClasses}>
        {configuration &&
          configuration.questions.map((question) => {
            const _answer = answersRef.current.find((answer) => answer.questionId === question.id);
            const _validationConfig = configuration?.fieldValidations?.find(
              (fieldValidation) => fieldValidation.answerId === _answer?.id
            );

            const _error = validationErrorMap[question.id];

            const hasQuestionError =
              errorObject &&
              errorObject.type === 'has-answer' &&
              !readOnly &&
              !isEmpty(configuration?.questions) &&
              !_answer;
            const questionStyles = clsx({ 'border border-error': hasQuestionError });

            return (
              <div key={question.id} className={questionStyles}>
                <QuestionnaireQuestion
                  id={id}
                  name={question.caption}
                  label={question.caption}
                  value={_answer}
                  readOnly={readOnly}
                  onChange={handleChange}
                  question={question}
                  configuration={configuration}
                  recordId={recordId}
                  registerPreSubmitHandler={registerPreSubmitHandler}
                  registerPostSubmitHandler={registerPostSubmitHandler}
                  saveRecord={saveRecord}
                  validationErrors={_error}
                  validationConfig={_validationConfig}
                  submitCount={submitCount}
                  required={required}
                  setFormButtonsState={setFormButtonsState}
                  getRecordData={getRecordData}
                  updateRecordDataToIncludeAttachments={updateRecordDataToIncludeAttachments}
                />
                {hasQuestionError && <ErrorLabel>{fieldValidationErrorMessage(question.caption)}</ErrorLabel>}
              </div>
            );
          })}
      </div>
      {showScores && configuration.type === 'NUMERIC_QUESTIONNAIRE' && (
        <NumericQuestionnaireScore answers={answersRef.current} questions={configuration.questions} />
      )}
      {showScores && configuration.type === 'TEXTUAL_QUESTIONNAIRE' && (
        <QuestionnaireScore
          answers={answersRef.current}
          answersConfig={configuration.answers}
          questions={configuration.questions}
        />
      )}
    </div>
  );
};

export default Questionnaire;
